import { useMemo } from 'react'

import { getFilingStatus } from '#dictionary/filingStatuses'
import { useShowProfileQuery } from '#profile/api'
import ProfileService from '#profile/ProfileService'
import type { ProfileDoc } from '#profile/schemas'
import { SurveyCollection } from '#survey'
import { useListSurveysQuery } from '#survey/api'
import type { Survey } from '#survey/schemas/survey'

import {
  type BuildContext,
  buildFinancialRecords,
  type BuildFinancialRecordsOptions,
  type FinancialRecordsBuild,
  type SubmissionInput,
} from './financialRecords/buildFinancialRecords'
import {
  buildMemberRetirementAges,
  type MemberRetirementAges,
} from './memberRetirementAges'

export type UseFinancialRecordsBuilderOptions = {
  /** Additional surveys to fold into the SurveyCollection (e.g. quest templates). */
  extraSurveys?: Survey[]
  /** Override the baseline retirement ages from the profile. */
  retirementAges?: MemberRetirementAges
  /** Build for this profile instead of the signed-in user's. */
  profileDoc?: ProfileDoc
}

/** `buildFinancialRecords` with `context` pre-bound. */
export type BuildFinancialRecordsFn = (
  submissionDoc: SubmissionInput,
  buildOpts?: BuildFinancialRecordsOptions
) => FinancialRecordsBuild

export type UseFinancialRecordsBuilderResult = {
  /** `null` until profile + surveys have loaded. */
  build: BuildFinancialRecordsFn | null
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  error: Error | null
}

const EMPTY_SURVEYS: Survey[] = []

/**
 * Prepares the `BuildContext` from the current user's profile and surveys,
 * and returns `buildFinancialRecords` curried with it. The caller iterates
 * over its own submissions and calls the returned function per submission.
 *
 * Options:
 * - `extraSurveys` — extra surveys to add to the collection (quest templates).
 * - `retirementAges` — override the baseline retirement ages from the profile.
 *   Used by the what-if scenario run in `useFinancialProjection`.
 *
 * TODO(perf): cache the build results. Consumers now include
 * `useFinancialProjection` (baseline + scenario) and `useTuningCandidates`,
 * and every new caller re-runs `buildFinancialRecords` per submission on each
 * render. Consider memoizing by `(submission, context)` — likely a WeakMap
 * keyed on the built `context` object, or moving the per-submission build
 * into a React Query cache. Bottleneck risk grows with submission count.
 */
export const useFinancialRecordsBuilder = (
  options: UseFinancialRecordsBuilderOptions = {}
): UseFinancialRecordsBuilderResult => {
  const { extraSurveys = EMPTY_SURVEYS, retirementAges, profileDoc } = options

  const profileQuery = useShowProfileQuery('default', {
    enabled: !profileDoc,
  })
  const surveysQuery = useListSurveysQuery()
  const profile = profileDoc ?? profileQuery.data

  const build = useMemo<BuildFinancialRecordsFn | null>(() => {
    if (!profile || !surveysQuery.data) {
      return null
    }
    const profileService = new ProfileService(profile)
    const context: BuildContext = {
      surveyCollection: new SurveyCollection([
        ...surveysQuery.data,
        ...extraSurveys,
      ]),
      members: profileService.allMembers,
      surveyVariablesInput: {
        maritalStatus: profileService.maritalStatus,
        filingStatus: getFilingStatus(profileService),
        minorChildrenCount: profileService.minorChildren.length,
        otherDependentsCount: profileService.otherDependents.length,
        projectionStartYear: profileService.projectionStartYear,
        person: profileService.person,
        partner: profileService.partner,
      },
      retirementAges:
        retirementAges ?? buildMemberRetirementAges(profileService.allMembers),
    }
    return (submissionDoc, buildOpts) =>
      buildFinancialRecords(context, submissionDoc, buildOpts)
  }, [profile, surveysQuery.data, extraSurveys, retirementAges])

  // A disabled query still reports what is cached under its key.
  const profileState = profileDoc
    ? { isLoading: false, isError: false, isSuccess: true, error: null }
    : profileQuery

  const isLoading = profileState.isLoading || surveysQuery.isLoading
  const isError = profileState.isError || surveysQuery.isError
  const isSuccess = profileState.isSuccess && surveysQuery.isSuccess
  const error = profileState.error ?? surveysQuery.error ?? null

  return {
    build,
    isLoading,
    isError,
    isSuccess,
    error,
  }
}
